/**
 * Nostr verification key + naddr utilities.
 *
 * Ported from treasures.to. Each unique art print binds a fresh Nostr
 * keypair to the creator's pubkey via an `naddr` coordinate, and the
 * printed QR encodes a claim URL of the form:
 *
 *   {origin}/{naddr}#verify={nsec}
 *
 * The nsec embedded in the URL is the *verification* key (a throwaway
 * keypair minted per print), NOT the creator's identity key. Scanning
 * the QR lets the finder prove possession and claim the print.
 */

import { nip19 } from "nostr-tools";
import { NSecSigner } from "@nostrify/nostrify";

/**
 * Kind used for the addressable print/treasure event. Matches the
 * treasures.to NIP-GC GEOCACHE kind so prints remain interoperable
 * with the existing claim flow. Override via app config if desired.
 */
export const PRINT_EVENT_KIND = 37516;

export interface VerificationKeyPair {
  privateKey: Uint8Array;
  publicKey: string;
  nsec: string;
  npub: string;
}

/** Generate a random 32-byte secret key via the Web Crypto API. */
function generateSecretKey(): Uint8Array {
  const key = new Uint8Array(32);
  crypto.getRandomValues(key);
  return key;
}

async function getPublicKeyFromSecret(secretKey: Uint8Array): Promise<string> {
  const signer = new NSecSigner(secretKey);
  return await signer.getPublicKey();
}

/** Mint a fresh verification key pair for a single print. */
export async function generateVerificationKeyPair(): Promise<VerificationKeyPair> {
  const privateKey = generateSecretKey();
  const publicKey = await getPublicKeyFromSecret(privateKey);

  return {
    privateKey,
    publicKey,
    nsec: nip19.nsecEncode(privateKey),
    npub: nip19.npubEncode(publicKey),
  };
}

/**
 * Encode an addressable coordinate (naddr) for a print.
 */
export function printToNaddr(
  pubkey: string,
  dTag: string,
  relays: string[] = [],
  kind: number = PRINT_EVENT_KIND,
): string {
  return nip19.naddrEncode({
    pubkey,
    kind,
    identifier: dTag,
    relays,
  });
}

/** Decode an naddr back into its components. */
export function naddrToPrint(naddr: string) {
  const decoded = nip19.decode(naddr);
  if (decoded.type !== "naddr") {
    throw new Error("Invalid naddr format");
  }
  return decoded.data;
}

/**
 * Build the standard claim URL embedded in each print's QR code.
 * Format: {origin}/{naddr}#verify={nsec}
 */
export function buildClaimUrl(origin: string, naddr: string, nsec: string): string {
  return `${origin.replace(/\/$/, "")}/${naddr}#verify=${nsec}`;
}

/** Generate a random hex string of the given length (default 6). */
export function generateCompactDTag(length: number = 6): string {
  const bytes = new Uint8Array(Math.ceil(length / 2));
  crypto.getRandomValues(bytes);
  return Array.from(bytes)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("")
    .substring(0, length);
}
